import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from '@/components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import ReviewerDashboard from './pages/ReviewerDashboard';
import SOPManagement from './pages/SOPManagement';
import CallUpload from './pages/CallUpload';
import BulkUpload from './pages/BulkUpload';
import AssessmentResults from './pages/AssessmentResults';
import Analytics from './pages/Analytics';
import TeamManagement from './pages/TeamManagement';
import Reports from './pages/Reports';
import IntegrationSettings from './pages/IntegrationSettings';
import LiveCallMonitor from './pages/LiveCallMonitor';
import AuditSheets from './pages/AuditSheets';
import ConductAudit from './pages/ConductAudit';
import AgentManagement from './pages/AgentManagement';
import UserManagement from './pages/UserManagement';
import AuthCallback from './pages/AuthCallback';
import NotificationSettings from './pages/NotificationSettings';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CreditPurchase from './pages/CreditPurchase';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import LandingPage from './pages/LandingPage';
import CapabilitiesPage from './pages/CapabilitiesPage';
import PricingPage from './pages/PricingPage';
import ReportingPage from './pages/ReportingPage';
import ContactPage from './pages/ContactPage';
import CompaniesConsole from './pages/CompaniesConsole';
import AgentScorecard from './pages/AgentScorecard';
import InviteAccept from './pages/InviteAccept';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(localStorage.getItem('workspace_company_id') || null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (workspaceId) {
        axios.defaults.headers.common['X-Workspace-Company-Id'] = workspaceId;
      }
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.removeItem('workspace_company_id');
    delete axios.defaults.headers.common['X-Workspace-Company-Id'];
    setWorkspaceId(null);
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('workspace_company_id');
    setToken(null);
    setUser(null);
    setWorkspaceId(null);
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Workspace-Company-Id'];
  };

  const switchWorkspace = (companyId) => {
    if (companyId) {
      localStorage.setItem('workspace_company_id', companyId);
      axios.defaults.headers.common['X-Workspace-Company-Id'] = companyId;
    } else {
      localStorage.removeItem('workspace_company_id');
      delete axios.defaults.headers.common['X-Workspace-Company-Id'];
    }
    setWorkspaceId(companyId);
    window.location.reload();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, workspaceId, switchWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredPermission, superAdminOnly, agentBlocked }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && !user.is_super_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (agentBlocked && user.role === 'agent') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredPermission && !user.is_super_admin && !(user.permissions || []).includes(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// AuthCallback Handler - detects session_id in URL hash
const AuthCallbackHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if URL has session_id in hash
    if (location.hash && location.hash.includes('session_id=')) {
      navigate('/auth/callback' + location.hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthCallbackHandler />
        <div className="App">
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/capabilities" element={<CapabilitiesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/reporting" element={<ReportingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<LoginPage initialTab="register" />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/invite/:token" element={<InviteAccept />} />

            {/* Protected Pages */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sops"
              element={
                <ProtectedRoute requiredPermission="manage_sops">
                  <SOPManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload"
              element={
                <ProtectedRoute requiredPermission="upload_calls">
                  <CallUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-upload"
              element={
                <ProtectedRoute requiredPermission="upload_calls">
                  <BulkUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teams"
              element={
                <ProtectedRoute requiredPermission="manage_teams">
                  <TeamManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermission="view_reports">
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute requiredPermission="manage_integrations">
                  <IntegrationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-monitor"
              element={
                <ProtectedRoute agentBlocked>
                  <LiveCallMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-sheets"
              element={
                <ProtectedRoute requiredPermission="manage_sops">
                  <AuditSheets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conduct-audit/:callId"
              element={
                <ProtectedRoute agentBlocked>
                  <ConductAudit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents"
              element={
                <ProtectedRoute requiredPermission="manage_agents">
                  <AgentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <ProtectedRoute requiredPermission="manage_users">
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <ProtectedRoute superAdminOnly>
                  <CompaniesConsole />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessment/:callId"
              element={
                <ProtectedRoute>
                  <AssessmentResults />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute requiredPermission="view_reports">
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notification-settings"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute superAdminOnly>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/credits"
              element={
                <ProtectedRoute agentBlocked>
                  <CreditPurchase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-cancel"
              element={
                <ProtectedRoute>
                  <PaymentCancel />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'agent') return <AgentScorecard />;
  return user?.role === 'admin' ? <AdminDashboard /> : <ReviewerDashboard />;
};

export default App;
export { API };